import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ApiError, useApi, type PickSummary, type PodcastDetail, type Rec } from '../api'
import { useIsMobile } from '../layout'
import { usePageTitle } from '../title'
import { fmtDate, fmtMonthYear, fmtPct, fmtPts, signClass } from '../format'
import { Chart } from '../components/Chart'
import { PerformanceNote } from '../components/Disclosures'
import { DirPill, Empty, ErrorState, Icon, Loading, NotFound, PodAv, Sparkline } from '../components/primitives'

const RECENT = 12

function ScoringNote({ stats }: { stats: PodcastDetail['stats'] }) {
  if (stats.pick_count === 0) return null
  return (
    <p className="t-sm" style={{ color: 'var(--ink-3)', margin: '8px 0 0' }} data-testid="scoring-note">
      Win rate and average return cover {stats.scored_count} priced buy/sell {stats.scored_count === 1 ? 'call' : 'calls'}; holds aren’t scored.
    </p>
  )
}

function PickStat({ label, pick, mobile }: { label: string; pick: PickSummary | null; mobile: boolean }) {
  const body = pick ? (
    <>
      <span className="tick" style={mobile ? undefined : { fontSize: 20 }}>{pick.ticker}</span>
      <span className={`t-num text-${signClass(pick.pick_return_pct) || 'up'}`} style={mobile ? undefined : { fontSize: 20, fontWeight: 600 }}>
        {fmtPct(pick.pick_return_pct)}
      </span>
    </>
  ) : (
    <span>—</span>
  )
  const when = pick ? `Recommended ${fmtDate(pick.episode_pub_date)}` : 'No priced calls yet'
  if (mobile) {
    const inner = (
      <>
        <span className="l">{label}</span>
        <span className="v">{body}</span>
        <span className="t-sm" style={{ color: 'var(--ink-3)', fontSize: 12 }}>{when}</span>
      </>
    )
    return pick ? <Link to={`/rec/${pick.id}`} className="m-stat" style={{ color: 'inherit', textDecoration: 'none' }}>{inner}</Link> : <div className="m-stat">{inner}</div>
  }
  return (
    <div className="card grow">
      <div className="t-xs mb-12">{label}</div>
      <div className="row ai-b g-10">{body}</div>
      <div className="t-sm" style={{ marginTop: 4 }}>{pick ? <Link to={`/rec/${pick.id}`} style={{ color: 'inherit' }}>{when}</Link> : when}</div>
    </div>
  )
}

export const PORTFOLIO_TITLE = 'Hypothetical: if you’d followed every pick'

export function Portfolio({ data, mobile }: { data: PodcastDetail; mobile: boolean }) {
  const curve = data.portfolio
  const last = curve.at(-1)
  // Draw SPY only when it covers the whole curve; a partial line would compare
  // different windows.
  const benchmark = curve.length > 0 && curve.every((p) => p.benchmark_pct != null) ? curve.map((p) => p.benchmark_pct!) : null
  const lead = benchmark && last ? last.return_pct - last.benchmark_pct! : null
  return (
    <>
      <div className="row ai-b g-8" style={{ margin: mobile ? '2px 0 8px' : '2px 0 0' }}>
        <span className={`t-num text-${signClass(last?.return_pct) || 'up'}`} style={{ fontSize: 24, fontWeight: 600 }}>
          {fmtPct(last?.return_pct)}
        </span>
        {curve.length > 0 && <span className="t-sm" style={{ color: 'var(--ink-3)' }}>since {fmtMonthYear(curve[0].ts)}</span>}
        {lead != null && (
          <span className={`t-sm text-${signClass(lead) || 'up'}`} style={{ marginLeft: 'auto' }} data-testid="vs-benchmark">
            {fmtPts(lead)} vs SPY ({fmtPct(last!.benchmark_pct)})
          </span>
        )}
      </div>
      {!mobile && <div style={{ height: 14 }} />}
      <div className={mobile ? 'm-chart' : undefined}>
        <Chart
          values={curve.map((p) => p.return_pct)}
          dates={curve.map((p) => p.ts)}
          height={mobile ? 170 : 200}
          variant={mobile ? 'mobile' : 'desktop'}
          formatValue={(v) => fmtPct(v)}
          tone={last == null ? 'flat' : last.return_pct >= 0 ? 'up' : 'down'}
          compare={benchmark ?? undefined}
          compareLabel="SPY"
        />
      </div>
      {benchmark && (
        <div className="chart-legend" data-testid="chart-legend">
          <span className="sw solid" />Picks
          <span className="sw dashed" />SPY, same days
        </div>
      )}
      <PerformanceNote benchmark={benchmark != null} />
    </>
  )
}

function pickPct(p: Rec) {
  return (
    <span className={`num t-num ${p.pick_return_pct == null ? '' : p.pick_return_pct >= 0 ? 'text-up' : 'text-down'}`} style={{ width: 68, textAlign: 'right' }}>
      {fmtPct(p.pick_return_pct)}
    </span>
  )
}

export function Podcast() {
  const { id = '' } = useParams()
  const mobile = useIsMobile()
  const { data, error, retry } = useApi<PodcastDetail>(api.podcast(id))
  usePageTitle(data?.podcast.name)
  const [showAll, setShowAll] = useState(false)

  if (error instanceof ApiError && error.status === 404) {
    return <NotFound what="Podcast"><Link to="/podcasts">All podcasts</Link></NotFound>
  }
  if (error) return <ErrorState error={error} retry={retry} />
  if (!data) return <Loading />

  const { podcast, stats, picks } = data
  const shown = showAll ? picks : picks.slice(0, RECENT)
  const sub = `${podcast.episodes_analyzed} episodes analyzed${podcast.tracked_since ? ` · tracked since ${fmtMonthYear(podcast.tracked_since)}` : ''}`
  const winRate = stats.win_rate_pct == null ? '—' : `${Math.round(stats.win_rate_pct)}%`
  const avgCls = signClass(stats.avg_return_pct)

  if (mobile) {
    return (
      <div data-testid="podcast-page">
        <div className="row ai-c" style={{ gap: 14, marginTop: 4 }}>
          <PodAv name={podcast.name} image={podcast.image_url} size="xl sq" />
          <div className="col g-4 grow">
            <div className="serif" style={{ fontSize: 26, fontWeight: 500, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{podcast.name}</div>
            <div className="t-sm" style={{ color: 'var(--ink-3)' }}>{sub}</div>
          </div>
        </div>
        <div className="m-stats" style={{ marginTop: 18 }}>
          <div className="m-stat"><span className="l">Picks</span><span className="v">{stats.pick_count}</span></div>
          <div className="m-stat"><span className="l">Win rate</span><span className="v" data-testid="win-rate">{winRate}</span></div>
          <div className="m-stat"><span className="l">Avg return</span><span className={`v ${avgCls}`}>{fmtPct(stats.avg_return_pct)}</span></div>
        </div>
        <ScoringNote stats={stats} />
        <div className="m-card" style={{ marginTop: 12 }}>
          <div className="t-xs">{PORTFOLIO_TITLE}</div>
          <Portfolio data={data} mobile />
        </div>
        <div className="m-stats" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
          <PickStat label="Best pick" pick={stats.best} mobile />
          <PickStat label="Worst pick" pick={stats.worst} mobile />
        </div>
        <div className="m-sec">
          <h3>Recent picks</h3>
          <span className="t-sm" style={{ color: 'var(--ink-3)' }}>{picks.length} total</span>
        </div>
        {picks.length === 0 ? (
          <Empty>No picks from this podcast yet.</Empty>
        ) : (
          <div className="m-list">
            {shown.map((p) => (
              <Link key={p.id} to={`/rec/${p.id}`} className="m-row">
                <div className="meta">
                  <span className="a"><span className="tick">{p.ticker}</span> <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>{p.company_name}</span></span>
                  <span className="b"><DirPill type={p.type} />{fmtDate(p.episode_pub_date)}</span>
                </div>
                <Sparkline values={p.spark.closes} w={56} h={22} />
                {pickPct(p)}
              </Link>
            ))}
          </div>
        )}
        {!showAll && picks.length > RECENT && (
          <button className="m-btn" style={{ marginTop: 12 }} onClick={() => setShowAll(true)}>See all {picks.length}</button>
        )}
      </div>
    )
  }

  return (
    <div data-testid="podcast-page">
      <div className="crumbs" style={{ marginBottom: 16 }}>
        <Link to="/">Home</Link>
        <span className="sep" aria-hidden>/</span>
        <Link to="/podcasts">Podcasts</Link>
        <span className="sep" aria-hidden>/</span>
        <span className="now">{podcast.name}</span>
      </div>
      <div className="host-head">
        <PodAv name={podcast.name} image={podcast.image_url} size="xl sq" />
        <div className="meta">
          <div className="name">{podcast.name}</div>
          <div className="sub">{sub}</div>
        </div>
      </div>
      <div className="metric-grid" data-cols="3">
        <div className="metric-tile"><span className="l">Total picks</span><span className="v">{stats.pick_count}</span></div>
        <div className="metric-tile"><span className="l">Win rate</span><span className="v" data-testid="win-rate">{winRate}</span></div>
        <div className="metric-tile"><span className="l">Avg return</span><span className={`v ${avgCls}`}>{fmtPct(stats.avg_return_pct)}</span></div>
      </div>
      <ScoringNote stats={stats} />
      <div className="chart-wrap mt-18">
        <div className="chart-head">
          <div className="col g-4">
            <span className="t-xs">{PORTFOLIO_TITLE}</span>
            <Portfolio data={data} mobile={false} />
          </div>
        </div>
      </div>
      <div className="card mt-18">
        <div className="row ai-c jc-sb mb-12">
          <h3 className="serif" style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>Recent picks</h3>
          {!showAll && picks.length > RECENT && (
            <button className="btn ghost" onClick={() => setShowAll(true)}>See all {picks.length}</button>
          )}
        </div>
        {picks.length === 0 ? (
          <Empty>No picks from this podcast yet.</Empty>
        ) : (
          shown.map((p) => (
            <Link key={p.id} to={`/rec/${p.id}`} className="mention" style={{ color: 'inherit', textDecoration: 'none' }}>
              <span className="tick" style={{ fontSize: 14, width: 58 }}>{p.ticker}</span>
              <DirPill type={p.type} />
              <div className="meta">
                <div className="pods">{p.company_name}</div>
                <div className="when">{fmtDate(p.episode_pub_date)}</div>
              </div>
              <Sparkline values={p.spark.closes} w={80} h={26} />
              {pickPct(p)}
              <Icon name="chevron" size={14} />
            </Link>
          ))
        )}
      </div>
      <div className="row g-12 mt-18">
        <PickStat label="Best pick" pick={stats.best} mobile={false} />
        <PickStat label="Worst pick" pick={stats.worst} mobile={false} />
      </div>
    </div>
  )
}
